import axios from "axios";
import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { API_URL } from "../../constants";
import { ZILLOW_URL_ERROR, looksLikeListingUrl, normalizeZillowUrl } from "../../utils/zillowUrl";

const Wrapper = styled.div`
  position: relative;
`;

const EntryRow = styled.div`
  display: flex;
  gap: 10px;
  align-items: stretch;

  @media (max-width: 560px) {
    flex-direction: column;
  }
`;

const ContinueButton = styled.button`
  flex: 0 0 auto;
  padding: 12px 18px;
  border: 1px solid var(--brand-primary, #0e7e7a);
  border-radius: 8px;
  background: var(--brand-primary, #0e7e7a);
  color: #fff;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const EntryFeedback = styled.div`
  margin-top: 8px;
  font-size: 0.9rem;
  color: #6c757d;
`;

const Input = styled.input`
  width: 100%;
  min-width: 0;
  padding: 12px 14px;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 8px;

  &:focus {
    outline: none;
    border-color: #0e7e7a;
    box-shadow: 0 0 0 3px rgba(14, 126, 122, 0.15);
  }

  &:disabled {
    color: #6b7280;
    background: #f3f4f6;
    cursor: not-allowed;
  }
`;

const Dropdown = styled.ul`
  position: absolute;
  z-index: 1000;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  margin: 0;
  padding: 4px 0;
  list-style: none;
  background: #ffffff;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  max-height: 280px;
  overflow-y: auto;
`;

const Option = styled.li`
  padding: 10px 14px;
  cursor: pointer;
  font-size: 0.95rem;

  &:hover,
  &[aria-selected="true"] {
    background: #f0f8f7;
  }
`;

const StatusRow = styled.li`
  padding: 10px 14px;
  color: #6c757d;
  font-size: 0.9rem;
`;

const DEBOUNCE_MS = 300;
const MIN_QUERY_LENGTH = 4;
const LOOKUP_TIMEOUT_MS = 15000;

/**
 * Address typeahead backed by /address-autocomplete/.
 *
 * Suggestions disambiguate same-named streets across cities (123 Main St,
 * Montgomery AL vs New York NY). Calls `onSelect(suggestion)` with
 * {display, zpid, city, state, zipcode} when the user picks one.
 *
 * Optional free-text passthrough: after a completed lookup returns no
 * suggestions, `onFreeText` can accept the typed address.
 *
 * @param {{
 *   placeholder?: string,
 *   onSelect: (suggestion: {display: string, zpid: number, city: string, state: string, zipcode: string}) => void,
 *   onFreeText?: (text: string) => void,
 *   onListingUrl?: (url: string) => void,
 *   continueLabel?: string,
 *   clearAfterSelect?: boolean,
 *   disabled?: boolean,
 *   id?: string,
 * }} props
 */
const AddressAutocomplete = ({
  placeholder,
  onSelect,
  onFreeText,
  onListingUrl,
  continueLabel,
  clearAfterSelect = false,
  disabled = false,
  id,
}) => {
  const [query, setQuery] = useState("");
  const [feedback, setFeedback] = useState("");
  const [suggestions, setSuggestions] = useState(
    /** @type {Array<{display: string, zpid: number, city: string, state: string, zipcode: string}>} */ ([])
  );
  const [open, setOpen] = useState(false);
  const [highlighted, setHighlighted] = useState(-1);
  const [searching, setSearching] = useState(false);
  const [completedQuery, setCompletedQuery] = useState("");
  const debounceRef = useRef(/** @type {ReturnType<typeof setTimeout> | null} */ (null));
  const latestRequestRef = useRef(0);
  const submittedRef = useRef(false);
  const inputRef = useRef(/** @type {HTMLInputElement | null} */ (null));
  const wrapperRef = useRef(/** @type {HTMLDivElement | null} */ (null));
  const listboxId = id ? `${id}-suggestions` : undefined;
  const feedbackId = id ? `${id}-feedback` : undefined;
  const isLinkInput = !!onListingUrl && looksLikeListingUrl(query);
  const invalidLink = isLinkInput && !normalizeZillowUrl(query);

  useEffect(() => {
    if (!disabled) submittedRef.current = false;
  }, [disabled]);

  useEffect(() => {
    /** @param {MouseEvent} event */
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(/** @type {Node} */ (event.target))) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      // Selecting a suggestion often navigates immediately — kill the pending
      // debounce and invalidate any in-flight request so nothing setStates
      // after unmount.
      if (debounceRef.current) clearTimeout(debounceRef.current);
      latestRequestRef.current += 1;
    };
  }, []);

  /** @param {string} value */
  const search = (value) => {
    const requestId = ++latestRequestRef.current;
    setSearching(true);
    setCompletedQuery("");
    setSuggestions([]);
    setOpen(true);
    setHighlighted(-1);
    axios
      .get(`${API_URL}/address-autocomplete/`, {
        params: { q: value },
        timeout: LOOKUP_TIMEOUT_MS,
      })
      .then((response) => {
        if (requestId !== latestRequestRef.current) return; // stale response
        setSuggestions(response.data.suggestions || []);
        setCompletedQuery(value);
        setOpen(true);
        setHighlighted(-1);
      })
      .catch(() => {
        if (requestId !== latestRequestRef.current) return;
        setSuggestions([]);
        setCompletedQuery(value);
        setOpen(true);
      })
      .finally(() => {
        if (requestId === latestRequestRef.current) setSearching(false);
      });
  };

  /** @param {import('react').ChangeEvent<HTMLInputElement>} event */
  const handleChange = (event) => {
    const value = event.target.value;
    setQuery(value);
    setFeedback("");
    submittedRef.current = false;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    // Invalidate an older response immediately, rather than allowing it to
    // reopen a stale result list during this query's debounce window.
    latestRequestRef.current += 1;
    setSearching(false);
    setCompletedQuery("");
    setSuggestions([]);
    if (value.trim().length < MIN_QUERY_LENGTH || (onListingUrl && looksLikeListingUrl(value))) {
      setSuggestions([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => search(value.trim()), DEBOUNCE_MS);
  };

  /** @param {{display: string, zpid: number, city: string, state: string, zipcode: string}} suggestion */
  const choose = (suggestion) => {
    if (disabled || submittedRef.current) return;
    submittedRef.current = true;
    setFeedback("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    latestRequestRef.current += 1;
    setQuery(clearAfterSelect ? "" : suggestion.display);
    setOpen(false);
    setSuggestions([]);
    setCompletedQuery("");
    onSelect(suggestion);
  };

  const submitFreeText = () => {
    const text = query.trim();
    if (disabled || submittedRef.current || !onFreeText || !text || isLinkInput) return;
    submittedRef.current = true;
    setFeedback("");
    if (debounceRef.current) clearTimeout(debounceRef.current);
    latestRequestRef.current += 1; // invalidate any in-flight search
    setOpen(false);
    setSuggestions([]);
    setCompletedQuery("");
    if (clearAfterSelect) setQuery("");
    onFreeText(text);
  };

  // Both Enter and the optional adjacent CTA use this component's current
  // query. A CTA never discards text or silently picks an ambiguous address.
  const submitEntry = () => {
    if (disabled || submittedRef.current) return;
    if (isLinkInput && onListingUrl) {
      const url = normalizeZillowUrl(query);
      if (!url) {
        setFeedback(ZILLOW_URL_ERROR);
        inputRef.current?.focus();
        return;
      }
      if (debounceRef.current) clearTimeout(debounceRef.current);
      latestRequestRef.current += 1;
      submittedRef.current = true;
      setOpen(false);
      setSuggestions([]);
      setCompletedQuery("");
      setFeedback("");
      onListingUrl(url);
      return;
    }
    if (suggestions.length > 0 && highlighted >= 0) {
      choose(suggestions[highlighted]);
      return;
    }
    if (
      onFreeText &&
      !searching &&
      suggestions.length === 0 &&
      query.trim().length >= MIN_QUERY_LENGTH &&
      completedQuery === query.trim()
    ) {
      submitFreeText();
      return;
    }
    inputRef.current?.focus();
    if (query.trim().length < MIN_QUERY_LENGTH) {
      setFeedback("Enter a property address or Zillow listing link to continue.");
    } else {
      setOpen(true);
      setFeedback(
        suggestions.length > 0
          ? "Choose the matching address below to continue."
          : "Searching for this address — you can enter the details manually if no match is found."
      );
    }
  };

  /** @param {import('react').KeyboardEvent<HTMLInputElement>} event */
  const handleKeyDown = (event) => {
    if (event.key === "Enter") {
      if (isLinkInput || continueLabel) {
        event.preventDefault();
        submitEntry();
        return;
      }
      if (open && suggestions.length > 0 && highlighted >= 0) {
        event.preventDefault();
        choose(suggestions[highlighted]);
      } else if (
        onFreeText &&
        open &&
        !searching &&
        suggestions.length === 0 &&
        completedQuery === query.trim()
      ) {
        // Only a completed zero-result lookup unlocks manual entry. Enter
        // during the debounce/request window must not bypass automation.
        event.preventDefault();
        submitFreeText();
      }
      return;
    }
    if (event.key === "Escape" && open) {
      setOpen(false);
      return;
    }
    if (!open || suggestions.length === 0) return;
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlighted((h) => Math.min(h + 1, suggestions.length - 1));
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlighted((h) => Math.max(h - 1, 0));
    }
  };

  const freeTextReady =
    !!onFreeText &&
    !isLinkInput &&
    open &&
    !searching &&
    suggestions.length === 0 &&
    completedQuery === query.trim();

  return (
    <Wrapper ref={wrapperRef}>
      <EntryRow>
        <Input
          ref={inputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-autocomplete="list"
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={
            open && highlighted >= 0 && id ? `${id}-suggestion-${highlighted}` : undefined
          }
          aria-busy={searching}
          aria-invalid={invalidLink || undefined}
          aria-describedby={invalidLink || feedback ? feedbackId : undefined}
          aria-label={placeholder || "Start typing the property address…"}
          autoComplete="off"
          value={query}
          disabled={disabled}
          placeholder={placeholder || "Start typing the property address…"}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            const completedFallback =
              !!onFreeText &&
              !searching &&
              completedQuery === query.trim() &&
              query.trim().length >= MIN_QUERY_LENGTH;
            if (suggestions.length > 0 || completedFallback) setOpen(true);
          }}
        />
        {(continueLabel || isLinkInput) && (
          <ContinueButton type="button" disabled={disabled} onClick={submitEntry}>
            {continueLabel || "Use Zillow listing"}
          </ContinueButton>
        )}
      </EntryRow>
      {(invalidLink || feedback) && (
        <EntryFeedback id={feedbackId} role={invalidLink ? "alert" : "status"}>
          {invalidLink ? ZILLOW_URL_ERROR : feedback}
        </EntryFeedback>
      )}
      {open && (
        // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA combobox pattern — a native <select> can't do typeahead
        <Dropdown id={listboxId} role="listbox">
          {suggestions.map((suggestion, index) => (
            // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA combobox option — part of the custom listbox above
            <Option
              role="option"
              id={id ? `${id}-suggestion-${index}` : undefined}
              key={suggestion.zpid}
              aria-selected={index === highlighted}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(suggestion)}
              onMouseEnter={() => setHighlighted(index)}
            >
              {suggestion.display}
            </Option>
          ))}
          {searching && suggestions.length === 0 && (
            // biome-ignore lint/a11y/useSemanticElements: WAI-ARIA combobox status inside the custom listbox
            <StatusRow role="option" aria-selected={false} aria-disabled="true" aria-live="polite">
              Searching…
            </StatusRow>
          )}
          {!searching && suggestions.length === 0 && (
            // biome-ignore lint/a11y/useSemanticElements: free-text fallback is an option in the custom listbox
            <StatusRow
              role="option"
              aria-selected={false}
              aria-disabled={!freeTextReady}
              tabIndex={freeTextReady ? 0 : undefined}
              onMouseDown={freeTextReady ? (event) => event.preventDefault() : undefined}
              onClick={freeTextReady ? submitFreeText : undefined}
              onKeyDown={
                freeTextReady
                  ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        submitFreeText();
                      }
                    }
                  : undefined
              }
            >
              {freeTextReady
                ? "No matches — continue with the address as typed"
                : "No matching addresses found."}
            </StatusRow>
          )}
        </Dropdown>
      )}
    </Wrapper>
  );
};

export default AddressAutocomplete;
